import AppLayout from '@risk-smart/themed-cloudscape-components/app-layout';
import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import Navigation from '@risksmart-app/components/src/navigation';
import { useNavMenu } from '@risksmart-app/components/src/navigation/useNavMenu';
import { useTools } from '@risksmart-app/components/src/tools/useTools';
import clsx from 'clsx';
import { type FC, type ReactNode, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router';
import ConnectedCount from 'src/components/connected-count';
import type { TCountOptions } from 'src/components/connected-count/ConnectedCount';
import HelpPanel from 'src/components/help-panel';
import NotificationsList from 'src/components/notifications-list';
import { Wizard } from 'src/components/wizard/Wizard';
import { useGetEntities } from 'src/hooks/queries/entity/useGetEntities';
import { useNavItems } from 'src/routes/useNavItems';

import { ChangeRequestLevels } from '@/components/change-request-levels/ChangeRequestLevels';
import { SidePanel } from '@/components/side-panel/SidePanel';
import { useSidePanelStore } from '@/components/side-panel/useSidePanelStore';
import { useCustomOrgLogo } from '@/hooks/useCustomOrgLogo';
import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';
import { logoutUrl } from '@/utils/urls';

import styles from './style.module.scss';

type Props = {
  children: ReactNode;
  panelContent?: ReactNode;
};

export const AuthenticatedAppLayout: FC<Props> = ({
  children,
  panelContent,
}) => {
  const [navigationOpen, setNavigationOpen] = useNavMenu();

  const navItems = useNavItems();
  const navigationWidth = navigationOpen ? 300 : 68;
  const maxContentWidth = 1440;
  const [toolsContent, setToolsContent] = useTools();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(['common']);
  const showEntitySelector = useIsFeatureVisibleToOrg('enterprise_risk');
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const { entityIds, setEntityIds } = useEntityFilter();
  const { data: entities } = useGetEntities();
  const entityOptions = useMemo(
    () => [
      { label: t('entity.global'), value: undefined },
      ...(entities?.entity
        // @TODO: remove filter once support for nested entities is added
        .filter((entity) => !entity.children || !entity.children.length)
        .map((entity) => ({
          label: entity.Name,
          value: entity.Id,
        })) ?? []),
    ],
    [entities, t]
  );

  let tools: ReactNode | undefined = undefined;
  let toolWidth = 350;
  const toolsType = toolsContent?.split(':')[0];
  const id = toolsContent?.split(':')[1];
  const secondaryId = toolsContent?.split(':')[2];
  switch (toolsType) {
    case 'help':
      tools = <HelpPanel />;
      break;
    case 'notifications':
      tools = <NotificationsList />;
      break;
    case 'page-content':
      tools = panelContent;
      break;
    case 'change-request':
      if (id) {
        tools = (
          <ChangeRequestLevels parentId={id} changeRequestId={secondaryId} />
        );
      }
      break;
    case 'wizard':
      tools = <Wizard />;
      break;
    case 'ai-assistant':
      tools = <HelpPanel />;
      toolWidth = 550;
      break;
    default:
      tools = undefined;
  }

  const getCustomLogo = useCustomOrgLogo();

  const {
    isOpen: isSidePanelOpen,
    close: closeSidePanel,
    mode: sidePanelMode,
    isWidePanel,
    locationChanged,
  } = useSidePanelStore();

  // Use refs to track previous values to determine what changed
  const prevChatOpen = useRef(isSidePanelOpen);
  const prevToolsContent = useRef(toolsContent);

  // Handle mutual exclusivity between chat and tools
  useEffect(() => {
    const chatJustOpened = isSidePanelOpen && !prevChatOpen.current;
    const toolsJustOpened = toolsContent && !prevToolsContent.current;

    if (chatJustOpened && toolsContent) {
      // Chat was just opened and tools are open, close tools
      setToolsContent(undefined);
    } else if (toolsJustOpened && isSidePanelOpen) {
      // Tools were just opened and chat is open, close chat
      closeSidePanel();
    }

    // Update refs
    prevChatOpen.current = isSidePanelOpen;
    prevToolsContent.current = toolsContent;
  }, [isSidePanelOpen, toolsContent, setToolsContent, closeSidePanel]);

  const location = useLocation();

  useEffect(() => {
    locationChanged();
  }, [location, locationChanged]);

  // TODO check `toolsHide` is still needed in UIv3
  // Using the `toolsHide` prop causes the UI to jump, using `visibility: hidden` instead
  return (
    <div
      className={clsx(
        'grid grid-rows-[100vh] overflow-y-hidden transition-[grid-template-columns] duration-500 ease-out',
        styles.sidePanel,
        !isSidePanelOpen && styles.sidePanelClosed,
        isSidePanelOpen && isWidePanel && styles.sidePanelWide,
        isSidePanelOpen && !isWidePanel && styles.sidePanelStandard
      )}
    >
      <Navigation
        showUserMenu={true}
        renderCount={(props) =>
          !trpcEnabled && (
            <ConnectedCount
              {...props}
              countName={props.countName as TCountOptions}
            />
          )
        }
        navItems={navItems}
        logoutUrl={logoutUrl()}
        navigationOpen={navigationOpen}
        setNavigationOpen={setNavigationOpen}
        customLogoUrl={getCustomLogo}
        showEntitySelector={showEntitySelector}
        entityOptions={entityOptions}
        entityFilter={entityIds}
        onEntityChange={(entityId: string | undefined) =>
          entityId ? setEntityIds([entityId]) : setEntityIds([])
        }
      />
      <div
        className={
          'bg-[var(--color-background-layout-main-ru5mpv)] overflow-y-scroll no-scrollbar'
        }
      >
        <AppLayout
          content={
            <div className={'flex h-full'}>
              <div className={clsx('flex-grow', styles.rsContent)}>
                {children}
              </div>
            </div>
          }
          maxContentWidth={maxContentWidth}
          disableContentPaddings
          navigationOpen={false}
          onNavigationChange={() => {
            // Do nothing. Setting just to avoid warning in console
          }}
          navigationHide={true}
          toolsWidth={toolWidth}
          tools={tools}
          toolsHide={!toolsContent}
          toolsOpen={!!toolsContent}
          onToolsChange={({ detail }) => {
            if (
              !detail.open &&
              searchParams.get('showRequest') === 'true' &&
              toolsType === 'change-request'
            ) {
              setSearchParams((prev) => {
                prev.set('showRequest', 'false');

                return prev;
              });
            }

            setToolsContent(detail.open ? 'help' : undefined);
          }}
          navigationWidth={navigationWidth}
        />
      </div>
      <SidePanel
        onClose={closeSidePanel}
        mode={sidePanelMode}
        isOpen={isSidePanelOpen}
      ></SidePanel>
    </div>
  );
};
